import { cn } from "#/lib/utils";

type Variant =
  | "flame"
  | "flame-bubble"
  | "dark"
  | "dark-outline"
  | "white"
  | "white-outline";

const bubbleVariants = new Set<Variant>([
  "flame-bubble",
  "dark",
  "dark-outline",
  "white",
  "white-outline",
]);

const variantStyles: Record<Variant, string> = {
  flame:
    "bg-flame text-white hover:bg-flame-dark shadow-lg shadow-flame/25",
  "flame-bubble": "cta-bubble cta-bubble-flame",
  dark: "cta-bubble cta-bubble-dark",
  "dark-outline": "cta-bubble cta-bubble-outline",
  white: "cta-bubble cta-bubble-white",
  "white-outline": "cta-bubble cta-bubble-white-outline",
};

export interface CTAButtonProps {
  children: React.ReactNode;
  variant?: Variant;
  href?: string;
  className?: string;
  type?: "button" | "submit";
  onClick?: () => void;
  size?: "default" | "lg";
  disabled?: boolean;
  target?: string;
  rel?: string;
  "aria-label"?: string;
}

export function CTAButton({
  children,
  variant = "flame",
  href,
  className,
  type = "button",
  onClick,
  size = "default",
  disabled,
  target,
  rel,
  ...rest
}: CTAButtonProps) {
  const isBubble = bubbleVariants.has(variant);

  const classes = cn(
    "inline-flex items-center justify-center gap-2 rounded-lg font-semibold cursor-pointer",
    !isBubble && "transition-all duration-200",
    size === "lg"
      ? "px-6 py-3.5 text-base sm:px-8 sm:py-4 sm:text-[1.0625rem]"
      : "px-5 py-2.5 text-sm",
    variantStyles[variant],
    className,
  );

  const content = isBubble ? (
    <span className="relative z-[1] inline-flex items-center gap-2">{children}</span>
  ) : (
    children
  );

  if (href) {
    return (
      <a href={href} target={target} rel={rel} className={classes} {...rest}>
        {content}
      </a>
    );
  }

  return (
    <button type={type} onClick={onClick} className={classes} disabled={disabled} {...rest}>
      {content}
    </button>
  );
}
